import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { CPLAT_API_ROUTES, usePost } from '@/api';
import { Button } from '@/components/atoms/PcButton';
import { useAppSelector } from '@/redux/store';
import EN_TO_KO_MARKET_NAME_MAP from '@/contents/englishToKoreanMarketNames';
import { useLog } from '@/hooks/V3/useLog';
import { CplatApiResponse } from '@/types/api';
import { RegisteredMarketInfo } from '@/types/api';
import { useToast } from '@/hooks/V3/useToast';
const COMMERCE_SOLUTION_URL = 'https://sell.smartstore.naver.com/#/commerce-solution/solution-list';
const SHOP_BY_URl =
  'https://admin.shopby.co.kr/pro/app/basic/list?mallNo=78585&appStatus=ACTIVE&page=1';
const CAFE24_URL = 'https://chdb57.cafe24.com/disp/admin/shop1/myapps/list';

const getUnregisterUrl = (marketName: string) => {
  switch (marketName) {
    case '스마트스토어':
      return COMMERCE_SOLUTION_URL;
    case '샵바이':
      return SHOP_BY_URl;
    case '카페24':
      return CAFE24_URL;
    default:
      return '';
  }
};

type UnregisterDialogContainerProps = {
  onClose: () => void;
  checkedMarket: RegisteredMarketInfo;
};

const UnregisterDialogContainer = ({ onClose, checkedMarket }: UnregisterDialogContainerProps) => {
  const user = useAppSelector((state) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  // 스마트스토어는 커머스솔루션 마켓에서 연동해제 해야 한다.
  // shopby 여기도 해결해야 한다.
  const isExternalRegisterMarket =
    checkedMarket.MarketNameEnglish === 'smartstore' ||
    checkedMarket.MarketNameEnglish === 'shopby' ||
    checkedMarket.MarketNameEnglish === 'cafe24';

  const { mutate: unregisterMarket, isPending: isUnregisterPending } = usePost(
    CPLAT_API_ROUTES.unregisterMarket,
  );

  const handleUnregisterMarket = () => {
    const { MarketId, VendorId, MarketNameEnglish, MarketAlias, MarketPassword } = checkedMarket;

    const body = {
      MarketName: MarketNameEnglish,
      MarketLoginInfo: JSON.stringify({ MarketName: MarketNameEnglish, MarketId, VendorId }),
      CplatToken: user.CplatToken,
    };

    unregisterMarket(body, {
      onSuccess(res) {
        const { code } = res.data as CplatApiResponse<undefined>;

        if (code === '200') {
          queryClient
            .refetchQueries({
              queryKey: [CPLAT_API_ROUTES.getRegisteredMarketInfo],
            })
            .then(() => {
              onClose();
            });
          setUserEventHistory({
            eventName: 'disconnect_market',
            eventValue: `${MarketNameEnglish} ${MarketAlias}`,
          });
        }

        if (code === '712') {
          showToast('warning', '연동 해제에 실패했어요. 잠시 후 다시 시도해주세요.');
          onClose();
        }
        if (code === '500') {
          showToast('warning', '연동 해제에 실패했어요. 잠시 후 다시 시도해주세요.');
          onClose();
        }
        if (code === '580') {
          showToast('warning', '연동 해제에 실패했어요. 잠시 후 다시 시도해주세요.');
          onClose();
        }
      },
    });
  };

  const handleUnregisterSmartStore = () => {
    onClose();
  };

  return isExternalRegisterMarket ? (
    <div className="flex flex-col items-center">
      <p className="text-gr-900 Title3S18">마켓 연동을 해제하시겠어요?</p>

      <p className="mt-[4px] text-left text-gr-700 Body5M14">
        {EN_TO_KO_MARKET_NAME_MAP[checkedMarket.MarketNameEnglish] || '마켓'} 연동 해제를 원하실
        경우
        <br />
        판매자 센터에서 솔루션을 해지해주세요
      </p>
      <div className="mt-[20px] flex w-full gap-[12px]">
        {/** TODO shopby 여기 링크도 수정해야 한다.  */}
        <Link to={getUnregisterUrl(checkedMarket.MarketName)} target="_blank">
          <Button onClick={handleUnregisterSmartStore} variant="muted" className="h-[44px] flex-1">
            확인
          </Button>
        </Link>
      </div>
    </div>
  ) : (
    <div className="flex flex-col items-start">
      <p className="text-gr-900 Title3S18">마켓 연동을 해제하시겠어요?</p>
      <p className="mt-[4px] text-left text-gr-700 Body5M14">
        더 이상 해당 마켓의 정보를 볼 수 없어요.
        <br />
        그래도 해지하시겠어요?
      </p>
      <div className="mt-[20px] flex w-full gap-[12px]">
        <Button
          disabled={isUnregisterPending}
          onClick={handleUnregisterMarket}
          variant="muted"
          className="h-[44px] flex-1"
        >
          연동 해제
        </Button>
        <Button onClick={onClose} className="h-[44px] flex-1">
          취소
        </Button>
      </div>
    </div>
  );
};

export default UnregisterDialogContainer;
